import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { ConfirmDialog } from "@components/ui/ConfirmDialog"
import { Field, SelectInput, TextInput } from "@components/ui/Field"
import { RowActions } from "@components/ui/RowActions"
import { SkeletonRows } from "@components/ui/Skeleton"
import { Table } from "@components/ui/Table"
import { useToast } from "@components/ui/Toaster"
import type { Institution } from "@features/institutions"
import {
  EditSchoolDialog,
  useCreateSchool,
  useDeleteSchool,
  useSchoolSubjects,
  useSchools,
  useSeedDefaultSubjects,
  type School,
} from "@features/schools"
import { zodResolver } from "@hookform/resolvers/zod"
import { apiErrorCode, apiErrorStatus } from "@lib/apiError"
import { formatNumber } from "@lib/intl"
import { logger } from "@lib/logger"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { useId, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { StepHeading } from "./StepHeading"

import { EDUCATION_LEVELS } from "@/constants/education"
import { PAGE_SIZE_MAX } from "@/constants/pagination"

const schema = z.object({
  name: z.string().trim().min(1, "required").max(200, "too_long"),
  city: z.string().trim().max(120, "too_long").optional(),
  education_level: z.enum(EDUCATION_LEVELS),
  seed: z.boolean(),
})
type FormInput = z.infer<typeof schema>
const EMPTY: FormInput = { name: "", city: "", education_level: "highschool", seed: true }

/** How many subjects a school has, or a one-click fix when it has none. */
function SubjectsCell({ school }: { school: School }) {
  const { t } = useTranslation("onboarding")
  const toast = useToast()
  const subjects = useSchoolSubjects(school.id)
  const seed = useSeedDefaultSubjects()
  if (!subjects.data) return <span className="text-meta">…</span>
  if (subjects.data.length > 0) return <>{formatNumber(subjects.data.length)}</>
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Badge variant="attention">{t("step.schools.no_subjects")}</Badge>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        loading={seed.isPending}
        onClick={() =>
          seed.mutate(school.id, {
            onError: (err) => {
              toast.error(t([`errors:${apiErrorCode(err)}`, "errors:internal_error"]))
              logger.error("onboarding.subject.seed_defaults.failed", {
                code: apiErrorCode(err),
                schoolId: school.id,
              })
            },
          })
        }
      >
        {t("step.schools.seed_one")}
        <span className="sr-only">, {school.name}</span>
      </Button>
    </span>
  )
}

/**
 * Step 2 — schools with their subjects (PRD-60 chunk D; they were two steps,
 * with a seed button per school and nothing editable once added). The list
 * is the server's: a school misspelled yesterday is fixed here today.
 */
export function StepSchools({
  institution,
  onBack,
  onNext,
}: {
  institution: Institution
  onBack: () => void
  onNext: () => void
}) {
  const { t } = useTranslation(["onboarding", "schools", "common", "errors"])
  const toast = useToast()
  const seedId = useId()
  // A new institution's schools, all on one page: the wizard adds them one
  // by one, and a hundred is past any onboarding (PAGE_SIZE_MAX).
  const schools = useSchools({ institutionId: institution.id, pageSize: PAGE_SIZE_MAX })
  const rows = schools.data?.items
  const create = useCreateSchool()
  const seed = useSeedDefaultSubjects()
  const del = useDeleteSchool()
  const [editingId, setEditingId] = useState<string | null>(null)
  const editing = rows?.find((s) => s.id === editingId) ?? null
  const [trashing, setTrashing] = useState<School | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<FormInput>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  const onSubmit = handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({
        institution_id: institution.id,
        name: values.name,
        education_level: values.education_level,
        ...(values.city ? { city: values.city } : {}),
      })
      if (values.seed) await seed.mutateAsync(created.id)
      toast.success(t("step.schools.added", { name: created.name }))
      logger.info("onboarding.school.created", { schoolId: created.id, seeded: values.seed })
      reset({ ...EMPTY, education_level: values.education_level, seed: values.seed })
      setFocus("name")
    } catch (err) {
      const code = apiErrorCode(err)
      toast.error(t([`errors:${code}`, "errors:internal_error"]))
      logger.error("onboarding.school.create.failed", { code, status: apiErrorStatus(err) })
    }
  })

  async function confirmTrash() {
    if (!trashing) return
    try {
      await del.mutateAsync(trashing.id)
      toast.success(t("schools:trash.success", { name: trashing.name }))
      logger.info("onboarding.school.trashed", { schoolId: trashing.id })
      setTrashing(null)
    } catch (err) {
      toast.error(t([`errors:${apiErrorCode(err)}`, "errors:internal_error"]))
      logger.error("onboarding.school.delete.failed", { code: apiErrorCode(err) })
    }
  }

  const count = schools.data?.total ?? 0

  return (
    <div className="space-y-5">
      <StepHeading title={t("step.schools.title")} help={t("step.schools.help")} />

      {!rows ? (
        <SkeletonRows count={2} rowClassName="h-14" />
      ) : count > 0 ? (
        <Table
          caption={t("step.schools.caption", { name: institution.name })}
          rows={rows}
          rowKey={(s) => s.id}
          identity={{
            header: t("schools:fields.school"),
            cell: (s) => (
              <span className="min-w-0">
                <span className="text-ink block truncate font-medium">{s.name}</span>
                <span className="text-meta block">
                  {t(`schools:education_level.${s.education_level}`)}
                </span>
              </span>
            ),
          }}
          columns={[
            {
              key: "subjects",
              header: t("step.schools.subjects"),
              cell: (s) => <SubjectsCell school={s} />,
            },
          ]}
          actions={(s) => (
            <RowActions
              subject={s.name}
              primary={{
                label: t("schools:actions.edit"),
                icon: <Pencil className="h-4 w-4" />,
                onClick: () => setEditingId(s.id),
              }}
              items={[
                {
                  label: t("schools:actions.trash"),
                  icon: <Trash2 className="h-4 w-4" />,
                  onClick: () => setTrashing(s),
                  danger: true,
                },
              ]}
            />
          )}
          actionsHeader={t("schools:fields.actions")}
        />
      ) : null}

      <form
        onSubmit={onSubmit}
        noValidate
        className="rounded-card border-border space-y-4 border border-dashed p-4"
      >
        <h3 className="text-ink text-sm font-semibold">
          {count > 0 ? t("step.schools.add_another") : t("step.schools.add_first")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("schools:fields.name")}
            required
            error={errors.name && t(`schools:form.error.${errors.name.message}`)}
          >
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                autoComplete="off"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                {...register("name")}
              />
            )}
          </Field>
          <Field label={t("schools:fields.city")}>
            {({ id }) => <TextInput id={id} autoComplete="off" {...register("city")} />}
          </Field>
        </div>
        <Field
          label={t("schools:fields.education_level")}
          required
          hint={t("schools:fields.education_level_hint")}
        >
          {({ id, describedBy }) => (
            <SelectInput id={id} aria-describedby={describedBy} {...register("education_level")}>
              {EDUCATION_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {t(`schools:education_level.${level}`)}
                </option>
              ))}
            </SelectInput>
          )}
        </Field>
        <label htmlFor={seedId} className="flex min-h-11 items-start gap-3 md:min-h-0">
          <input
            id={seedId}
            type="checkbox"
            className="accent-accent mt-0.5 h-5 w-5 shrink-0 md:h-4 md:w-4"
            {...register("seed")}
          />
          <span>
            <span className="text-ink block text-sm font-medium">{t("step.schools.seed")}</span>
            <span className="text-meta block">{t("step.schools.seed_hint")}</span>
          </span>
        </label>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="secondary"
            loading={create.isPending || seed.isPending}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            {t("step.schools.add")}
          </Button>
        </div>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={onBack}>
          {t("common:actions.back")}
        </Button>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {count === 0 && <p className="text-meta">{t("step.schools.need_one")}</p>}
          <Button onClick={onNext} disabled={count === 0}>
            {t("common:actions.next")}
          </Button>
        </div>
      </div>

      {editing && (
        <EditSchoolDialog
          school={editing}
          institutionName={institution.name}
          onClose={() => setEditingId(null)}
        />
      )}

      <ConfirmDialog
        open={!!trashing}
        title={t("schools:trash.title", { name: trashing?.name ?? "" })}
        message={t("schools:trash.confirm", { count: trashing?.professor_count ?? 0 })}
        confirmLabel={t("schools:actions.trash")}
        loading={del.isPending}
        onConfirm={confirmTrash}
        onClose={() => setTrashing(null)}
      />
    </div>
  )
}
